import * as React from 'react';
import { Fragment } from 'react';
import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { Modifier, OptionsGeneric } from '@popperjs/core';
import styled, { withTheme } from 'styled-components';
import { propStyles, style } from '../utils/propStyles';
import { verticalAlign } from '../utils/customPropStyles';
import Icon from './Icon';
import ClickLayer from './ClickLayer';
import { AccessibilityStore, EntityStore } from '../stores';
import { Popper } from '../utils/Popper';
import { AnalyticsContext } from '../models/analyticsContext';

const arrowFill = style({
    prop: 'bgColor',
    key: 'color',
    cssProperty: 'fill',
});

const arrowStroke = style({
    prop: 'borderColor',
    key: 'color',
    cssProperty: 'stroke',
});

const ArrowIcon = () => (
    <svg width="24px" height="24px" viewBox="0 0 24 24">
        <path className="arrow__path" d="M7,10L12,15L17,10H7Z" />
    </svg>
);

const Arrow = styled<any, any>('div')`
    position: absolute;
    height: 24px;
    width: 24px;
    .arrow__path {
        fill: inherit;
        stroke: inherit;
        stroke-width: 1px;
        stroke-dasharray: 14, 7, 0;
    }
`;

const TooltipWrapper = styled<any, any>('div')`
    ${propStyles};
    ${verticalAlign};
`;

TooltipWrapper.defaultProps = {
    display: 'inline-block',
};

const TooltipIconButton = styled<any, any>('button')`
    ${propStyles};
    outline: none;
`;

TooltipIconButton.defaultProps = {
    display: 'inline',
    color: 'primary',
    bgColor: 'transparent',
    borderStyle: 'none',
    cursor: 'pointer',
};

const TooltipIcon = styled(Icon)``;

TooltipIcon.defaultProps = {
    height: '16px',
    width: '16px',
};

const TooltipCharacter = styled<any, any>('div')`
    ${propStyles};
`;

TooltipCharacter.defaultProps = {
    fontSize: '9px',
    minWidth: '1.5em',
    margin: '0 0.5em',
    padding: '0.15em 0.25em',
    color: 'white',
    bgColor: 'primary',
    borderRadius: '1.5em',
    textAlign: 'center',
};

const TooltipContent = styled<any, any>('div')`
    ${propStyles};
    cursor: pointer;

    ${Arrow} {
        ${arrowFill};
        ${arrowStroke};
        transform-origin: center;
    }

    &[data-popper-placement='top'] ${Arrow} {
        bottom: -14px;
    }

    &[data-popper-placement='bottom'] ${Arrow} {
        top: -14px;
        transform: rotate(180deg);
    }

    &[data-popper-placement='left'] ${Arrow} {
        right: -14px;
        transform: rotate(-90deg);
    }

    &[data-popper-placement='right'] ${Arrow} {
        left: -14px;
        transform: rotate(90deg);
    }
`;

TooltipContent.defaultProps = {
    minWidth: '200px',
    maxWidth: '300px',
    color: 'body',
    bgColor: 'white',
    fontSize: 'xs',
    fontFamily: 'body',
    fontWeight: 'regular',
    textAlign: 'center',
    padding: 'sm',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'gray500',
    borderRadius: 'sm',
    boxShadow: 'sm',
    zIndex: 'popover',
};

interface TooltipProps {
    theme?: { [property: string]: string | number };
    tooltipCharacter?: string;
    iconFilename?: string;
    showContent?: boolean;
    content: Array<string>;
    verticalAlign?: string;
    accessibilityContext?: string;
    entityStore?: EntityStore;
    accessibilityStore?: AccessibilityStore;
    analyticsContext?: AnalyticsContext;
}

@inject('accessibilityStore', 'dataStore', 'entityStore')
@observer
class Tooltip extends Popper<TooltipProps> {
    popper;
    tooltipNode;
    tooltipContentNode;
    arrowNode;
    @observable show = false;

    popperOptions: Partial<OptionsGeneric<Partial<Modifier<any, any>>>> = {
        placement: 'top',
        modifiers: [
            {
                name: 'flip',
                enabled: false,
            },
            {
                name: 'offset',
                options: {
                    offset: [0, 5],
                },
            },
        ],
    };

    get popperEnabled() {
        return true;
    }

    toggleTooltip = e => {
        const {
            dataStore,
            entityStore,
            content,
            analyticsContext,
        } = this.props;

        e.stopPropagation();
        e.preventDefault();
        this.show = !this.show;
        this.updatePopper();

        if (this.show) {
            dataStore.logDataLayer('tooltip_opened', {
                relatedEntityId: entityStore?.value?.clientCode || 'Global',
                sourceContext: content,
                source: analyticsContext?.source,
                tooltipCode: analyticsContext?.tooltipCode || 'information',
            });
        }
    };

    stopPropagation = e => {
        e.stopPropagation();
    };

    @computed
    get content() {
        return this.props.content || [];
    }

    @computed
    get hasContent() {
        return this.content.length > 0;
    }

    @computed
    get tooltipContent() {
        const { theme, analyticsContext } = this.props;
        const { metricContext } = analyticsContext;
        const { content } = this;

        // Do not use display: 'none' here as it causes the popper to fail when trying to work out the init position
        const hiddenStyle: React.CSSProperties = {
            visibility: 'hidden',
            pointerEvents: 'none',
        };
        return (
            <TooltipContent
                ref={this.setPopupNodeRef}
                onClick={this.toggleTooltip}
                {...theme.TooltipContent}
                style={(!this.show && hiddenStyle) || {}}
                data-metric-id={`tooltip-content-${metricContext}`}
            >
                {content.map((c, index) => (
                    <div
                        onClick={this.stopPropagation}
                        key={index}
                        dangerouslySetInnerHTML={{
                            __html: c,
                        }}
                    />
                ))}
                <Arrow
                    ref={this.setArrowNodeRef}
                    className="popper__arrow"
                    x-arrow=""
                >
                    <ArrowIcon />
                </Arrow>
            </TooltipContent>
        );
    }

    render() {
        const {
            theme,
            tooltipCharacter,
            showContent = true,
            accessibilityContext,
            analyticsContext,
            accessibilityStore,
            iconFilename = 'InformationIcon',
            ...props
        } = this.props;

        const { metricContext } = analyticsContext;

        const { hasContent } = this;

        if (hasContent) {
            return (
                <TooltipWrapper {...props}>
                    <TooltipIconButton
                        aria-label={accessibilityStore.buttonAccessibilityValue(
                            'TooltipIconButton'
                        )}
                        ref={this.setAnchorNodeRef}
                        onClick={showContent ? this.toggleTooltip : () => null}
                        data-metric-id={`tooltip-${metricContext}`}
                        cursor={showContent ? 'pointer' : 'default'}
                        {...theme.TooltipIconButton}
                    >
                        {tooltipCharacter ? (
                            <TooltipCharacter {...theme.TooltipCharacter}>
                                {tooltipCharacter}
                            </TooltipCharacter>
                        ) : (
                            <TooltipIcon
                                fileName={iconFilename}
                                {...theme.TooltipIcon}
                                accessibilityContext={accessibilityContext}
                            />
                        )}
                    </TooltipIconButton>
                    {showContent && (
                        <Fragment>
                            {this.show && (
                                <ClickLayer
                                    data-metric-id={'tooltip-clickLayer'}
                                    onClick={this.toggleTooltip}
                                />
                            )}
                            {this.tooltipContent}
                        </Fragment>
                    )}
                </TooltipWrapper>
            );
        }

        return null;
    }
}

export default withTheme(Tooltip);



// WEBPACK FOOTER //
// ./src/Components/Tooltip.tsx